import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { map, mergeMap, catchError, filter, tap } from 'rxjs/operators';
import { EnvService } from '../../services/env.service';
import {
  FetchServiceOptions,
  GetServiceOptionsFailed,
  GetServiceOptionsSuccess,
} from './service-options.actions';
import { Store } from '@ngrx/store';
import { ServiceOptionsResponse } from './service-options.model';
import { GetLabelsSuccess } from '../labels';
import { ClearStates } from '../states';
import { mapAndSortCountries } from '../utils';
import { isSdfCardFlow } from 'src/app/config/app-config';
import { FxRequest } from '../fee-quote/types';
import { pathOr } from 'ramda';
import { GetFxRateAction } from '../fee-quote/fee-quote.actions';

@Injectable()
export class ServiceOptionsEffects {
  private optionsUrl(endpoint: string): string {
    // TODO:TIM Requirements changed, so this can probably be refactored into a single string value, and just interpolate the v2 or v3
    return endpoint === 'v3'
      ? `${this.envService.apiUrl}/v3/config-data/serviceoptions`
      : `${this.envService.apiUrl}/v2/config-data/serviceoptions`;
  }

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private envService: EnvService,
    private store: Store
  ) {}

  public getServiceOptiosn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetLabelsSuccess),
      filter(({ getLabelsResponse, endpoint }) => {
        return getLabelsResponse && getLabelsResponse.operation !== undefined;
      }),
      mergeMap(({ getLabelsResponse, endpoint }: any) => {
        return this.http
          .get(
            `${this.optionsUrl(endpoint)}?operation=${
              getLabelsResponse.operation
            }`
          )
          .pipe(
            map((data: ServiceOptionsResponse) => {
              this.getFxRateDetails(
                data,
                getLabelsResponse.operation,
                endpoint
              );
              return GetServiceOptionsSuccess({
                serviceOptionsResponse: this.setServiceOptions(
                  data,
                  getLabelsResponse
                ),
                endpoint,
              });
            }),
            catchError((error) => of(GetServiceOptionsFailed({ error })))
          );
      })
    )
  );

  public fetchServiceOptiosn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FetchServiceOptions),
      tap(() => this.store.dispatch(ClearStates())),
      mergeMap(({ getOptionsRequest, endpoint }) => {
        return this.http
          .get(
            `${this.optionsUrl(endpoint)}?operation=${
              getOptionsRequest.operation
            }&userSelectedCountry=${getOptionsRequest.selectedWithdrawCountry}`
          )
          .pipe(
            map((data: ServiceOptionsResponse) => {
              // data = serviceOptionsMock;
              const options = data && data?.serviceOptions;
              if (options && options[0]) {
                data = { ...data, isCashOutAllowed: 'true' };
              } else {
                data = { ...data, isCashOutAllowed: 'false' };
              }
              data = { ...data };
              this.getFxRateDetails(
                data,
                getOptionsRequest.operation,
                endpoint
              );
              return GetServiceOptionsSuccess({
                serviceOptionsResponse: data,
                endpoint: endpoint,
              });
            }),
            catchError((error) => of(GetServiceOptionsFailed({ error })))
          );
      })
    )
  );

  getFxRateDetails(data: any, flow: string, endpoint: string) {
    const serviceOptions = pathOr(null, ['serviceOptions', 0], data);
    if (serviceOptions) {
      let pay: FxRequest = {
        targetAudience: 'CONSUMER_FACING',
        userLanguage: 'en-US',
        operation: flow,
      };

      if (flow === 'withdraw') {
        pay['receiveCurrencyCode'] = serviceOptions.localCurrencyCode
          ? serviceOptions.localCurrencyCode
          : serviceOptions.payoutCurrencyCodes[0];
        pay['sendCurrencyCode'] = 'USD';
        pay['destinationCountryCode'] = data.withdrawCountry;
        pay['originatingCountryCode'] = 'USA';
      } else {
        pay['sendCurrencyCode'] = serviceOptions?.sendCurrencyCodes[0];
        pay['destinationCountryCode'] = 'USA';
        pay['originatingCountryCode'] = data.depositCountry;
      }

      // console.log("FX RATe Payload : ", pay)
      this.store.dispatch(GetFxRateAction({ fxRequest: pay, endpoint }));
    }
  }

  setServiceOptions(data: any, getLabelsResponse: any): ServiceOptionsResponse {
    const options = data && data?.serviceOptions;
    if (options && options[0]) {
      data = { ...data, isCashOutAllowed: 'true' };
    } else {
      data = { ...data, isCashOutAllowed: 'false' };
    }
    const countriesList = data?.cashOutCountries || data?.cashInCountries;
    const countriesAvailable = mapAndSortCountries(
      countriesList,
      getLabelsResponse.state.stellar.country
    );
    data = { ...data, countriesDetails: countriesAvailable };
    return data;
  }
}
